import { PaginatedResponse } from '@app/api/api.type';
import { PARAM_GOOGLE_CLICK_ID } from '@app/gtag/google.constant';
import { PARAM_MICROSOFT_CLICK_ID } from '@app/gtag/microsoft.constant';
import { apiService, validateAndAssert } from '@app/services/api-service';
import { QRCodeInDtoOverride } from '@app/swagger-override-types';
import {
  QrCodeAdminInDto,
  QRCodeAdminOutDto,
  QRCodeBulkCreationInDto,
  QrCodeMultiDeleteInDto,
} from '@app/swagger-types';
import { pageableData } from '@app/utils/yup.utils';
import { APIQRCodeQueryParams } from '../qr-code.types';
import { QrDtoMapper } from './qr-code.mapper';
import { string } from 'yup';
import { QRCodeOutDtoSchema, QRCodePublicOutDtoSchema } from './qr-code.schema.api';

export abstract class QRCodeApi {
  static async create(dto: QRCodeInDtoOverride) {
    return validateAndAssert(apiService.post('/private/qr-code', dto), QRCodeOutDtoSchema);
  }
  static async createBulk({ csv }: { csv: File }) {
    const formData = new FormData();
    formData.append('file', csv);
    return apiService.post('/private/qr-code/bulk', formData);
    // TODO validate response schema
    // return validateAndAssert(apiService.post('/private/qr-code/bulk', formData), QRCodeOutDtoSchema);
  }
  static async createBulkJSON(dto: QRCodeBulkCreationInDto) {
    // TODO validate response schema
    return apiService.post('/private/qr-code/bulk/json', dto);
  }
  static async claim({
    qrId,
    googleClickId,
    microsoftClickId,
  }: {
    qrId: string;
    googleClickId?: string;
    microsoftClickId?: string;
  }) {
    const params = new URLSearchParams();
    if (googleClickId) {
      params.set(PARAM_GOOGLE_CLICK_ID, googleClickId);
    }
    if (microsoftClickId) {
      params.set(PARAM_MICROSOFT_CLICK_ID, microsoftClickId);
    }
    const maybeClickIdParams = params.toString() ? `?${params.toString()}` : '';
    return validateAndAssert(apiService.put(`/private/qr-code/claim/${qrId}${maybeClickIdParams}`), QRCodeOutDtoSchema);
  }
  static async getAll(params: APIQRCodeQueryParams) {
    return validateAndAssert(apiService.get('/private/qr-code', { params }), pageableData(QRCodeOutDtoSchema));
  }
  static async delete(id: string) {
    return apiService.delete(`/private/qr-code/${id}`);
  }
  static async multiDelete(dto: QrCodeMultiDeleteInDto) {
    return apiService.put('/private/qr-code/multi-delete', dto);
  }
  static async getById(id: string) {
    const res = await validateAndAssert(apiService.get(`/private/qr-code/${id.toUpperCase()}`), QRCodeOutDtoSchema);
    QrDtoMapper.mutateResponseForForm(res);
    return res;
  }

  static async update({ id, ...dto }: Partial<QRCodeInDtoOverride> & { id: string }) {
    QrDtoMapper.mutateFormForRequest(dto);
    return validateAndAssert(apiService.patch(`/private/qr-code/${id}`, dto), QRCodeOutDtoSchema);
  }

  static async updateName({ id, name }: { name: string; id: string }) {
    return validateAndAssert(
      apiService.patch(`/private/qr-code/${id}/rename`, null, { params: { name, id } }),
      QRCodeOutDtoSchema
    );
  }
  static async getPublicById(qrId: string) {
    const res = await validateAndAssert(
      apiService.get(`/public/qr-code/${qrId.toUpperCase()}`),
      QRCodePublicOutDtoSchema
    );
    QrDtoMapper.mutateResponseForForm(res);
    return res;
  }
  static async registerDownload(qrId: string) {
    await apiService.post(`/public/qr-code/${qrId.toUpperCase()}/register-download`);
  }

  static async validateGTIN(gtin: string) {
    await validateAndAssert(
      apiService.get(`/public/qr-code/validate/gtin`, { params: { gtin } }),
      string().transform((curr, orig) => (orig === '' ? undefined : curr))
    );
    return;
  }

  /* #region  /**=========== ADMIN =========== */
  static async adminGetAll(
    params: APIQRCodeQueryParams & {
      afterDate?: string;
      idStartsWith?: string;
      exactMatchById?: string;
      exactMatchByEmail?: string;
    }
  ) {
    // TODO validate dto
    const { data } = await apiService.get<PaginatedResponse<QRCodeAdminOutDto>>('/private/admin/qr-codes', { params });
    return data;
  }

  static async adminGetById(id: string) {
    // TODO validate dto
    const { data } = await apiService.get<QRCodeAdminOutDto>(`/private/admin/qr-codes/${id}`);
    return data;
  }

  static async adminPatchById(id: string, payload: QrCodeAdminInDto) {
    // TODO validate dto
    const { data } = await apiService.patch<QRCodeAdminOutDto>(`/private/admin/qr-codes/${id}`, payload);
    return data;
  }
  /* #endregion  /**======== ADMIN =========== */
}
